import { Kbd } from '@chakra-ui/react';
import { LocalStorageId } from '@resistapp/client/components/shared/local-storage';
import { getCtrlKeyName } from '@resistapp/client/utils/general';
import { MetricMode } from '@resistapp/common/types';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';

export enum ContextualTipId {
  MAP_LEGEND_INFO_ARGI = 'MAP_LEGEND_INFO_ARGI',
  MAP_LEGEND_INFO_RISK = 'MAP_LEGEND_INFO_RISK',
  MAP_LEGEND_INFO_REDUCTION = 'MAP_LEGEND_INFO_REDUCTION',
  MAP_CLICK_AREA = 'MAP_CLICK_AREA',
  MAP_CLICK_MARKER = 'MAP_CLICK_MARKER',
  SITE_FOCUS_GENE_OR_ANTIBIOTIC = 'SITE_FOCUS_GENE_OR_ANTIBIOTIC',
  OVERVIEW_SELECT_PREVIOUS_MONTHS = 'OVERVIEW_SELECT_PREVIOUS_MONTHS',
  OVERVIEW_SECONDARY_METRIC_LABEL = 'OVERVIEW_SECONDARY_METRIC_LABEL',
  RESEARCH_GENE_LEGEND_FOCUS = 'RESEARCH_GENE_LEGEND_FOCUS',
  RESEARCH_SAMPLE_LEGEND_SELECT = 'RESEARCH_SAMPLE_LEGEND_SELECT',
  RESEARCH_SAMPLE_LEGEND_MULTI = 'RESEARCH_SAMPLE_LEGEND_MULTI',
}

interface TipState {
  interacted?: boolean;
  dismissedAt?: number;
  /** Timestamp until which the tip should stay dismissed (ms since epoch) */
  dismissedUntil?: number;
  /** How many times the tip has been dismissed without interaction */
  dismissalCount?: number;
  /** Number of times the user has interacted with the feature linked to the tip */
  interactionCount?: number;
  lastCompletedAt?: number;
}

type ContextaulTipStates = Partial<Record<ContextualTipId, TipState>>;

const snoozeDuration = 30 * 60 * 1000;

interface ContextualTipConfig {
  id: ContextualTipId;
  /** If specified, tip is only considered when the given metric mode is active */
  metricMode?: MetricMode;
  message: React.ReactNode;
  /** Delay in ms before starting the attention-drawing animation */
  animationDelay: number;
  /** Order in which the tip should appear relative to others */
  order?: number;
  /** Milliseconds to wait after the previous tip (any) was completed before showing this tip */
  delayAfterPreviousMs?: number;
}

export const contextualTipConfigs: ContextualTipConfig[] = [
  // --- Overview ---
  {
    id: ContextualTipId.MAP_LEGEND_INFO_ARGI,
    metricMode: MetricMode.ARGI,
    animationDelay: 10000,
    message: 'What is ARGI?',
    order: 1,
  },
  {
    id: ContextualTipId.MAP_LEGEND_INFO_RISK,
    metricMode: MetricMode.RISK,
    animationDelay: 10000,
    message: 'What is the definition of risk?',
    order: 1,
  },
  {
    id: ContextualTipId.MAP_LEGEND_INFO_REDUCTION,
    metricMode: MetricMode.REDUCTION,
    animationDelay: 10000,
    message: 'What is log-10 change?',
    order: 1,
  },
  {
    id: ContextualTipId.MAP_CLICK_AREA,
    // Applies when map shows colored areas (no specific metric mode constraint)
    message: 'Click on a colored area to zoom in!',
    animationDelay: 10000,
    order: 2,
    delayAfterPreviousMs: 3000,
  },
  {
    id: ContextualTipId.MAP_CLICK_MARKER,
    // Applies when map shows site markers (no specific metric mode constraint)
    message: 'Click to see site details!',
    animationDelay: 10000,
    order: 3,
    delayAfterPreviousMs: 3000,
  },
  {
    id: ContextualTipId.OVERVIEW_SECONDARY_METRIC_LABEL,
    // Applies in all metric modes (handled dynamically)
    message: 'Switch between raw and treated!',
    animationDelay: 10000,
    order: 4,
    delayAfterPreviousMs: 3000,
  },
  {
    id: ContextualTipId.SITE_FOCUS_GENE_OR_ANTIBIOTIC,
    // Applies in all metric modes, but only in site view where detail boxes are visible.
    message: 'Click to focus on a gene or antibiotic!',
    animationDelay: 10000,
    order: 5,
    delayAfterPreviousMs: 3000,
  },
  // {
  //   id: ContextualTipId.OVERVIEW_SELECT_PREVIOUS_MONTHS,
  //   // Applies to all metric modes, so metricMode undefined
  //   message: 'You can inspect other months',
  //   animationDelay: 3000,
  //   order: 6,
  //   delayAfterPreviousMs: 3000,
  // },
  // --- Research view ---
  {
    id: ContextualTipId.RESEARCH_GENE_LEGEND_FOCUS,
    // Applies to research view gene legend regardless of metric mode
    message: <span>Click to focus on a gene class!</span>,
    animationDelay: 10000,
    order: 1,
  },
  {
    id: ContextualTipId.RESEARCH_SAMPLE_LEGEND_SELECT,
    message: <span>Click to select a sample!</span>,
    animationDelay: 10000,
    order: 2,
    delayAfterPreviousMs: 2000,
  },
  {
    id: ContextualTipId.RESEARCH_SAMPLE_LEGEND_MULTI,
    message: (
      <span>
        <Kbd>{getCtrlKeyName()}</Kbd> or <Kbd>Shift</Kbd> + click to select multiple
      </span>
    ),
    animationDelay: 10000,
    order: 3,
    delayAfterPreviousMs: 2000,
  },
];

interface ContextualTipContextData {
  states: ContextaulTipStates;
  enabledMap: Partial<Record<ContextualTipId, boolean>>;
  setTipEnabled: (id: ContextualTipId, enabled: boolean) => void;
  markInteraction: (id: ContextualTipId) => void;
  dismissTip: (id: ContextualTipId) => void;
  /** Timestamp until which all tips are snoozed (ms since epoch). */
  tipsSnoozedUntil?: number;
}

const ContextualTipContext = createContext<ContextualTipContextData | undefined>(undefined);

export function ContextualTipProvider({ children }: { children: React.ReactNode }) {
  const [states, setStates] = useLocalStorageState<ContextaulTipStates>(LocalStorageId.contextualTipsState, {
    defaultValue: {},
  });

  const [enabledMap, setEnabledMap] = useState<Partial<Record<ContextualTipId, boolean>>>({});

  // When any tip is snoozed, we store the timestamp until which ALL tips should be hidden.
  const [tipsSnoozedUntil, setTipsSnoozedUntil] = useLocalStorageState<number | undefined>(
    LocalStorageId.contextualTipsSnoozedUntil,
    {
      defaultValue: undefined,
    },
  );

  const setTipEnabled = useCallback((id: ContextualTipId, enabled: boolean) => {
    setEnabledMap(current => ({ ...current, [id]: enabled }));
  }, []);

  const markInteraction = useCallback(
    (id: ContextualTipId) => {
      setStates(current => {
        const prev = current[id] ?? {};
        const nextCount = (prev.interactionCount ?? 0) + 1;
        return {
          ...current,
          [id]: { ...prev, interacted: true, interactionCount: nextCount, lastCompletedAt: Date.now() },
        };
      });
    },
    [setStates],
  );

  const dismissTip = useCallback(
    (id: ContextualTipId) => {
      setStates(current => ({
        ...current,
        [id]: {
          ...(current[id] ?? {}),
          dismissedAt: Date.now(),
          // Keep track of dismissal metadata, but do NOT set per-tip dismissedUntil.
          dismissalCount: (current[id]?.dismissalCount ?? 0) + 1,
          lastCompletedAt: Date.now(),
        },
      }));

      // Snooze ALL tips for the next 30 minutes (global behaviour)
      setTipsSnoozedUntil(Date.now() + snoozeDuration);
    },
    [setStates, setTipsSnoozedUntil],
  );

  return (
    <ContextualTipContext.Provider
      value={{ states, enabledMap, setTipEnabled, markInteraction, dismissTip, tipsSnoozedUntil }}
    >
      {children}
    </ContextualTipContext.Provider>
  );
}

export function useContextualTipContext() {
  const ctx = useContext(ContextualTipContext);
  if (!ctx) {
    throw new Error('useContextualTipContext must be used within ContextualTipProvider');
  }
  return ctx;
}

// helper to get timestamp when any tip (other than the candidate) was last completed
function getLastCompletedTimestamp(states: ContextaulTipStates, excludeId?: ContextualTipId): number {
  let latest = 0;
  for (const [id, state] of Object.entries(states)) {
    if (excludeId && id === excludeId.toString()) continue;
    const t = state.lastCompletedAt ?? state.dismissedAt ?? 0;
    if (t > latest) latest = t;
  }
  return latest;
}

export function useActiveContextualTip(metricMode: MetricMode): ContextualTipConfig | undefined {
  const { states, enabledMap, tipsSnoozedUntil } = useContextualTipContext();

  // ------------------------------------------------------------
  // Set up a lightweight timer so that the hook re-evaluates even
  // when there are no React state changes from user interaction.
  // This allows tips that become eligible after a delay to appear
  // automatically.
  // ------------------------------------------------------------
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    // Update roughly once per second – cheap and sufficient.
    const id = window.setInterval(() => {
      forceUpdate(t => (t + 1) % 60); // simple tick counter
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  // Respect global snooze window – if active, suppress all tips.
  if (tipsSnoozedUntil && Date.now() < tipsSnoozedUntil) {
    return undefined;
  }

  const candidates = contextualTipConfigs.filter(
    c =>
      // Tip must match metric mode if defined
      (!c.metricMode || c.metricMode.toString() === metricMode.toString()) &&
      // Tip not already completed
      !states[c.id]?.interacted &&
      // Tip enabled – tips are shown only when explicitly enabled by the page/component rendering them
      enabledMap[c.id] === true,
  );

  // Sort by explicit order, then by dismissedAt timestamp so that dismissed tips re-appear in FIFO order
  candidates.sort((a, b) => {
    const orderDiff = (a.order ?? 0) - (b.order ?? 0);
    if (orderDiff !== 0) return orderDiff;
    return (states[a.id]?.dismissedAt ?? 0) - (states[b.id]?.dismissedAt ?? 0);
  });

  for (const tip of candidates) {
    const state = states[tip.id];

    // Respect temporary dismissal window
    if (state?.dismissedUntil && Date.now() < state.dismissedUntil) {
      continue;
    }

    if (tip.delayAfterPreviousMs) {
      const lastCompleted = getLastCompletedTimestamp(states, tip.id);
      if (lastCompleted === 0) {
        // No previous tips completed yet
        continue;
      }
      if (Date.now() - lastCompleted < tip.delayAfterPreviousMs) {
        // Wait until delay has passed
        continue;
      }
    }

    return tip;
  }

  return undefined;
}

export function useContextualTip(id: ContextualTipId, metricMode: MetricMode) {
  const { states, markInteraction, dismissTip } = useContextualTipContext();
  const active = useActiveContextualTip(metricMode);
  // Guard against there being no active tip (all completed)
  if (!active) {
    return {
      visible: false,
      message: '',
      animationDelay: undefined,
      markInteraction: () => undefined,
      dismiss: () => undefined,
      showDismiss: false,
    } as const;
  }
  const visible = active.id === id;
  const { message, animationDelay } = active;

  // Allow snoozing at any time until the user actually interacts with the tip.
  const showDismiss = !states[id]?.interacted;

  return {
    visible,
    message,
    animationDelay,
    markInteraction: () => {
      markInteraction(id);
    },
    dismiss: () => {
      dismissTip(id);
    },
    showDismiss,
  } as const;
}
